import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller<HTMLElement> {
  static values = {
    group: String,
    animation: { type: Number, default: 150 },
    handle: { type: String, default: ".drag-handle" },
  };

  static targets = ["container"];

  declare readonly groupValue: string;
  declare readonly animationValue: number;
  declare readonly handleValue: string;
  declare readonly containerTarget: HTMLElement;
  declare readonly hasContainerTarget: boolean;

  private sortable: Sortable | null = null;
  private visibilityObserver: MutationObserver | null = null;

  connect() {
    // Only keep a live Sortable while this block is actually visible. In the
    // survey builder the options block is pre-rendered inside every (collapsed)
    // question card; a nested Sortable there corrupts the parent question
    // drag-sort (duplicated ghost rows). Defer init until the card is expanded
    // and the type picker reveals the options. See the colocated test.
    this.syncSortable();
    this.observeVisibility();
  }

  disconnect() {
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    this.destroySortable();
  }

  itemAddedHandler() {
    // Reinitialize sortable when new items are added — only while live, so we
    // never spin one up on a hidden block.
    if (this.sortable) {
      this.destroySortable();
      this.initializeSortable();
    }
  }

  // --- visibility-aware lifecycle -----------------------------------------

  private observeVisibility() {
    this.visibilityObserver = new MutationObserver(() => this.syncSortable());

    // The type picker toggles this element's inline `display`.
    this.visibilityObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // The question card toggles its form wrapper's `hidden` class on
    // expand/collapse. Outside the builder (e.g. the programs form) there is
    // no such ancestor and the block is simply visible from the start.
    const card = this.element.closest('[data-question-card-target="form"]');
    if (card) {
      this.visibilityObserver.observe(card, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }

  private syncSortable() {
    if (this.isVisible()) {
      if (!this.sortable) this.initializeSortable();
    } else {
      this.destroySortable();
    }
  }

  private isVisible(): boolean {
    // Hidden by the type picker (no option-based type selected) ...
    if (this.element.style.display === "none") return false;
    // ... or sitting inside a collapsed card / any hidden ancestor.
    return !this.element.closest(".hidden");
  }

  private destroySortable() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  private initializeSortable() {
    // Use the container target if available, otherwise find it
    const container = this.hasContainerTarget
      ? this.containerTarget
      : (this.element.querySelector(
          '[data-nested-form-target="container"]',
        ) as HTMLElement);

    if (!container) {
      return;
    }

    this.sortable = new Sortable(container, {
      group: this.groupValue,
      animation: this.animationValue,
      handle: this.handleValue,
      draggable: ".nested-fields",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: this.onEnd.bind(this),
    });
  }

  private onEnd(event: Sortable.SortableEvent) {
    if (event.oldIndex === event.newIndex) {
      return;
    }

    // Always update form field positions for sortable forms
    this.updateFormPositions();
  }

  private updateFormPositions() {
    const container = this.hasContainerTarget
      ? this.containerTarget
      : (this.element.querySelector(
          '[data-nested-form-target="container"]',
        ) as HTMLElement);

    if (!container) {
      return;
    }

    const items = container.querySelectorAll(
      ".nested-fields:not([style*='display: none'])",
    );

    items.forEach((item, index) => {
      const positionField = item.querySelector(
        'input[name*="[position]"]',
      ) as HTMLInputElement;
      if (positionField) {
        const newValue = (index + 1).toString();
        positionField.value = newValue;
      } else {
        console.warn(`No position field found for item ${index}`);
      }
    });
  }
}
